import React from 'react';
import PropTypes from 'prop-types';
import {Set} from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import {reduce, round} from 'lodash';
import ReactCrop from 'react-image-crop';
import download from 'downloadjs';

import {Banner, Button, ButtonGroup, Modal, Dialogue, Heading} from '@albert-io/atomic';

import {callAction} from 'client/framework';
import supplementEditorActions from 'client/Supplements/SupplementEditor/SupplementEditor.actions';
import supplementEditorStore from 'client/Supplements/SupplementEditor/SupplementEditor.store';
import TextInput from 'client/generic/Forms/TextInput/TextInput.react';
import NumberInput from 'client/generic/Forms/NumberInput/NumberInput.react';
import UploadButton from 'client/generic/UploadButton/UploadButton.react';
import {AuthoringSupplement as AuthoringSupplementModelV1} from 'resources/GeneratedModels/AuthoringSupplement/AuthoringSupplementModel.v1';

import Image from './Image.react';

import './image-editor.scss';
import 'react-image-crop/dist/ReactCrop.css';

export default class ImageEditor extends React.Component {
  static propTypes = {
    supplement: PropTypes.instanceOf(AuthoringSupplementModelV1),
    onChange: PropTypes.func,
    errors: ImmutablePropTypes.map
  };

  constructor(props) {
    super(props);
    this.state = {
      showModal: false
    };
  }

  UNSAFE_componentWillMount() {
    const {supplement} = this.props;
    const caption = supplement.getContent().get('caption', '');
    const alttext = supplement.getContent().get('alttext', '');
    callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, [
      {
        setter: 'setCaption',
        value: caption
      },
      {
        setter: 'setAlttext',
        value: alttext
      }
    ]);
  }

  clearImageModalStoreName = 'SupplementImageEditor--ClearImageModal';

  showModal = () => {
    this.setState({
      showModal: true
    });
  };

  closeModal = () => {
    this.setState({
      showModal: false
    });
  };

  handleImageSizeChange = (e) => {
    let value = e.target.value ? parseInt(e.target.value, 10) : 100;
    if (value > 100) {
      value = 100;
    } else if (value < 1) {
      value = 1;
    }
    callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, {
      setter: e.target.dataset.setterMethod,
      value
    });
  };

  uploadImage = (e) => {
    const element = e.target;
    callAction(supplementEditorActions.UPLOAD_FILE, {
      name: element.name,
      setter: 'setImageUrl',
      file: element.files[0]
    });
    this.closeModal();
  };

  onCrop = (reactCropData) => {
    const crop = reduce(
      reactCropData,
      (formatted, value, key) => {
        if (key === 'aspect') {
          return formatted;
        }
        formatted[key] = round(value);
        return formatted;
      },
      {}
    );

    callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, {
      setter: 'setCropValues',
      value: crop
    });
  };

  downloadImage = () => {
    download(this.props.supplement.getImageLocation().href);
  };

  clearImage() {
    const keySet = Set(['x', 'y', 'width', 'height', 'image']);
    const updatedContent = supplementEditorStore
      .getActiveSupplement()
      .getContent()
      .filterNot((value, key) => keySet.has(key));
    callAction(supplementEditorActions.MODIFY_ACTIVE_SUPPLEMENT, {
      setter: 'setContent',
      value: updatedContent
    });
    callAction(supplementEditorActions.VALIDATE_SUPPLEMENT, ['imageUrl']);
  }

  render() {
    const {errors, supplement} = this.props;
    const hasImage = supplement.getImageLocation().path;
    const source = supplement.getImageLocation().href;
    const crop = supplement.getCropValues().toJS();

    const uploadManager = hasImage ? (
      <div>
        {supplement.mightBePDF() ? (
          <Banner theme='danger'>
            Based on the filename of this supplement, it looks like it might be a PDF. A PDF
            uploaded as an image supplement will be automatically reformatted to a PNG – this may
            degrade quality. We suggest uploading this asset as a PNG or JPEG.
          </Banner>
        ) : null}
        <ReactCrop src={source} onComplete={this.onCrop} crop={crop} />
        <Image supplement={supplement} withZoom={false} />
        <ButtonGroup>
          <Button onClick={this.showModal} color='secondary'>
            Replace image
          </Button>
          <Button onClick={this.downloadImage} color='secondary'>
            Download image
          </Button>
        </ButtonGroup>
      </div>
    ) : (
      <UploadButton
        accept='image/*'
        disabled={supplementEditorStore.isUploadPending()}
        errorMessage={errors.get('imageUrl', null)}
        handleUpload={this.uploadImage}
      >
        Choose File
      </UploadButton>
    );
    const modal = this.state.showModal ? (
      <Modal title='Are you sure you want to replace this image?' handleClose={this.closeModal}>
        {({CloseButtonWrapper, modalContentStyle}) => (
          <Dialogue
            inModal
            title='Are you sure you want to replace this image?'
            className={modalContentStyle}
            handleClose={this.closeModal}
          >
            <Dialogue.Body>
              This action cannot be undone and will impact all questions using this supplement. All
              questions will be updated with the new image.
            </Dialogue.Body>
            <Dialogue.BtnGroup>
              <CloseButtonWrapper>
                <Button color='secondary'>Cancel</Button>
              </CloseButtonWrapper>
              <UploadButton
                accept='image/*'
                disabled={supplementEditorStore.isUploadPending()}
                errorMessage={errors.get('imageUrl', null)}
                handleUpload={(e) => {
                  this.clearImage();
                  this.uploadImage(e);
                }}
              >
                Choose File
              </UploadButton>
            </Dialogue.BtnGroup>
          </Dialogue>
        )}
      </Modal>
    ) : null;
    return (
      <div className='image-editor'>
        <div className='image-editor__fieldset'>
          <Heading>Image Supplement Details</Heading>
          <TextInput
            className='image-editor__input'
            data-setter-method='setName'
            error={Boolean(errors.get('name'))}
            errorMessage={errors.get('name', null)}
            label='Image Name'
            name='name'
            onChange={this.props.onChange}
            placeholder=''
            value={supplement.getName()}
          />
          <TextInput
            className='image-editor__input'
            data-setter-method='setCaption'
            error={Boolean(errors.get('caption'))}
            errorMessage={errors.get('caption', null)}
            label='Caption'
            name='caption'
            onChange={this.props.onChange}
            placeholder=''
            value={supplement.getCaption()}
          />
          <TextInput
            className='image-editor__input'
            data-setter-method='setAlttext'
            error={Boolean(errors.get('alttext'))}
            errorMessage={errors.get('alttext', null)}
            label='Alt text'
            name='alttext'
            onChange={this.props.onChange}
            placeholder=''
            value={supplement.getAlttext()}
          />
          <NumberInput
            className='image-editor__input'
            data-setter-method='setImageSize'
            label='Scale (%)'
            name='imageSize'
            min={1}
            max={100}
            error={Boolean(errors.get('imageSize'))}
            errorMessage={errors.get('imageSize', null)}
            onChange={this.handleImageSizeChange}
            placeholder='100%'
            value={supplement.getImageSize()}
          />
        </div>
        <div className='image-editor__input image-editor__input--upload-manager'>
          {uploadManager}
        </div>
        {modal}
      </div>
    );
  }
}
